import { useState, useEffect, useRef, useMemo } from "react";
import {
  SourceAdminstration,
  RunFrequency,
  SystemAuthorization,
  ExactVatCode,
  RunDetails,
  SynchronisationSchema,
} from "../types.ts";
import {
  fetchExactSystemAuthorizations,
  fetchExactAdministraties,
  fetchExactVATCodes,
  fetchEasyflexAdministrations,
  fetchEasyflexVATCODES,
  fetchEasyflexJournals,
  fetchRunswithDetails,
  fetchRunDetails,
} from "../services/ApiService.ts";
import { useApplicationInfoContext } from "../context/ApplicationInfoContext.tsx";
import { useUser } from "../context/UserContext";


export function useEasyflexAdministraties() {
  const { user } = useUser();
  const [easyflexAdministraties, setEasyflexAdministraties] = useState<SourceAdminstration[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user?.administrationCodes?.length) {
      setLoading(false);
      setError("Geen administratie beschikbaar");
      return;
    }

    const getEasyflexAdministraties = async () => {
      setLoading(true);
      try {
        const data = await fetchEasyflexAdministrations();
        setEasyflexAdministraties(data);
      } catch (err) {
        console.error("Failed to fetch Easyflex administraties:", err);
        setError("Failed to fetch data.");
      } finally {
        setLoading(false);
      }
    };

    getEasyflexAdministraties();
  }, [user?.administrationCodes]);

  return { easyflexAdministraties, loading, error };
}

export function useExactSystemAuthorizations(tenantWide: boolean = false) {
  const { user } = useUser();
  const [systemAuthorizations, setSystemAuthorizations] = useState<
    SystemAuthorization[]
  >([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const loadSystemAuthorizations = async () => {
    if (!user?.administrationCodes?.length) {
      setLoading(false);
      setError("Geen administratie beschikbaar");
      return;
    }

    setLoading(true);
    try {
      const data = await fetchExactSystemAuthorizations(tenantWide);
      setSystemAuthorizations(data ?? []);
      setError(null);
    } catch (err) {
      console.error("Failed to fetch Exact Online destination system authorizations:", err);
      setError("Failed to fetch data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSystemAuthorizations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.administrationCodes, tenantWide]);

  return { systemAuthorizations, loading, error, refresh: loadSystemAuthorizations };
}

export function useExactAdministraties(systemAuthorizationId: number) {
  const { user } = useUser();
  const [exactAdministraties, setExactAdministraties] = useState<SourceAdminstration[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user?.administrationCodes?.length || !systemAuthorizationId) {
      setLoading(false);
      if (!systemAuthorizationId) setError("Exact Online authenticatie ontbreekt");
      else setError("Geen administratie beschikbaar");
      return;
    }

    const getExactAdministraties = async () => {
      setLoading(true);
      try {
        const response = await fetchExactAdministraties(systemAuthorizationId);
        setExactAdministraties((response.data as SourceAdminstration[]) ?? []);
      } catch (err) {
        console.error("Failed to fetch Exact Online administraties:", err);
        setError("Failed to fetch data.");
      } finally {
        setLoading(false);
      }
    };

    getExactAdministraties();
  }, [user?.administrationCodes, systemAuthorizationId]);

  return { exactAdministraties, loading, error };
}

export function useEasyflexVATCodes() {
  const { user } = useUser();
  const [easyflexVATs, setEasyflexVATs] = useState<SourceAdminstration[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user?.administrationCodes?.length) {
      setLoading(false);
      setError("Geen administratie beschikbaar");
      return;
    }

    const getEasyflexVATs = async () => {
      setLoading(true);
      try {
        const data = await fetchEasyflexVATCODES();
        setEasyflexVATs(data);
      } catch (err) {
        console.error("Failed to fetch Easyflex VATs:", err);
        setError("Failed to fetch data.");
      } finally {
        setLoading(false);
      }
    };

    getEasyflexVATs();
  }, [user?.administrationCodes]);

  return { easyflexVATs, loading, error };
}

export function useEasyflexJournals() {
  const { user } = useUser();
  const [easyflexJournals, setEasyflexJournals] = useState<SourceAdminstration[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user?.administrationCodes?.length) {
      setLoading(false);
      setError("Geen administratie beschikbaar");
      return;
    }

    const getEasyflexJournals = async () => {
      setLoading(true);
      try {
        const data = await fetchEasyflexJournals();
        setEasyflexJournals(data);
      } catch (err) {
        console.error("Failed to fetch Easyflex journals:", err);
        setError("Failed to fetch data.");
      } finally {
        setLoading(false);
      }
    };

    getEasyflexJournals();
  }, [user?.administrationCodes]);

  return { easyflexJournals, loading, error };
}

export function useExactVATCodes(
  division: number,
  systemAuthorizationId: number,
  cachedData?: ExactVatCode[]
) {
  const { user } = useUser();
  const [exactVATCodes, setExactVATCodes] = useState<ExactVatCode[]>(cachedData ?? []);
  const [loading, setLoading] = useState<boolean>(!cachedData?.length);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Skip fetch if we already have cached data
    if (cachedData && cachedData.length > 0) {
      setExactVATCodes(cachedData);
      setLoading(false);
      return;
    }

    if (!user?.administrationCodes?.length || !systemAuthorizationId) {
      setLoading(false);
      setError("Geen administratie beschikbaar");
      return;
    }

    const getExactVATCodes = async () => {
      setLoading(true);
      try {
        const response = await fetchExactVATCodes(division, systemAuthorizationId);
        setExactVATCodes((response.data as ExactVatCode[]) ?? []);
      } catch (err) {
        console.error("Failed to fetch Exact Online VAT codes:", err);
        setError("Failed to fetch exact vat codes data.");
      } finally {
        setLoading(false);
      }
    };
    getExactVATCodes();
  }, [user?.administrationCodes, division, systemAuthorizationId, cachedData]);

  return { exactVATCodes, loading, error };
}

export function getFrequentieData() {
  // eslint-disable-next-line react-hooks/rules-of-hooks -- called only from component bodies, so the hooks keep a stable order
  const [frequentieData] = useState<RunFrequency[]>([
    RunFrequency.WEEKLY,
    RunFrequency.DAILY,
    RunFrequency.MONTHLY,
  ]);
  return { frequentieData };
}

export function getFrequencyLabel(frequency: RunFrequency): string {
  const labels: Record<RunFrequency, string> = {
    [RunFrequency.DAILY]: "Dagelijks",
    [RunFrequency.WEEKLY]: "Wekelijks",
    [RunFrequency.MONTHLY]: "Maandelijks",
  };
  return labels[frequency];
}

const dayLabels: Record<number, string> = {
  0: "Maandag",
  1: "Dinsdag",
  2: "Woensdag",
  3: "Donderdag",
  4: "Vrijdag",
  5: "Zaterdag",
  6: "Zondag",
};

export function getDayLabel(dayId: number): string {
  return dayLabels[dayId] ?? "";
}

export function formatSyncSchedule(schema: SynchronisationSchema): string {
  const frequency = getFrequencyLabel(schema.runFrequency as RunFrequency);
  const time = `${schema.runHour.toString().padStart(2, "0")}:${schema.runMinute.toString().padStart(2, "0")}`;
  const showDay = schema.runFrequency !== RunFrequency.DAILY && schema.runDay !== null;
  const day = showDay ? ` op ${getDayLabel(schema.runDay!).toLowerCase()}` : "";
  return `${frequency} om ${time}${day}`;
}

export function getDayList() {
  // eslint-disable-next-line react-hooks/rules-of-hooks -- called only from component bodies, so the hooks keep a stable order
  const [dayList] = useState(
    Object.entries(dayLabels).map(([id, label]) => ({ id: Number(id), label }))
  );
  return { dayList };
}

export function getSyncData(limit?: number, pollingIntervalMs: number = 5000) {
  // eslint-disable-next-line react-hooks/rules-of-hooks -- called only from component bodies, so the hooks keep a stable order
  const { user } = useUser();
  // eslint-disable-next-line react-hooks/rules-of-hooks -- called only from component bodies, so the hooks keep a stable order
  const [syncData, setSyncData] = useState<RunDetails[]>([]);
  // eslint-disable-next-line react-hooks/rules-of-hooks -- called only from component bodies, so the hooks keep a stable order
  const [loading, setLoading] = useState(true);
  // eslint-disable-next-line react-hooks/rules-of-hooks -- called only from component bodies, so the hooks keep a stable order
  const dataRef = useRef<RunDetails[]>([]);
  dataRef.current = syncData;

  // eslint-disable-next-line react-hooks/rules-of-hooks -- called only from component bodies, so the hooks keep a stable order
  const activeRunIds = useMemo(
    () =>
      syncData
        .filter((r) => r.state === "RUNNING" || r.state === "SCHEDULED")
        .map((r) => r.id),
    [syncData]
  );
  const activeRunIdsKey = activeRunIds.join(",");

  // Initial full fetch
  // eslint-disable-next-line react-hooks/rules-of-hooks -- called only from component bodies, so the hooks keep a stable order
  useEffect(() => {
    if (!user?.administrationCodes?.length) {
      setSyncData([]);
      setLoading(false);
      return;
    }

    let mounted = true;
    setLoading(true);

    const fetchInitialData = async () => {
      try {
        const runData = await fetchRunswithDetails(limit);
        if (mounted) setSyncData(runData);
      } catch (error) {
        console.error("Error fetching sync table data:", error);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchInitialData();

    return () => {
      mounted = false;
    };
  }, [user?.administrationCodes, limit]);

  // Poll only active runs
  // eslint-disable-next-line react-hooks/rules-of-hooks -- called only from component bodies, so the hooks keep a stable order
  useEffect(() => {
    if (pollingIntervalMs <= 0 || activeRunIds.length === 0) return;

    const intervalId = setInterval(async () => {
      const current = dataRef.current;
      const activeRuns = current.filter(
        (r) => r.state === "RUNNING" || r.state === "SCHEDULED"
      );

      if (activeRuns.length === 0) return;

      try {
        const updates = await Promise.all(
          activeRuns.map((r) => fetchRunDetails(r.id))
        );

        const updateMap = new Map(updates.map((u) => [u.id, u]));
        setSyncData(current.map((r) => updateMap.get(r.id) ?? r));
      } catch (error) {
        console.error("Error polling active runs:", error);
      }
    }, pollingIntervalMs);

    return () => clearInterval(intervalId);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- listing this would re-run the effect on changes it should ignore
  }, [activeRunIdsKey, pollingIntervalMs]);

  const data = limit != null ? syncData.slice(0, limit) : syncData;
  return { data, loading };
}

export const useConnectionData = (systemAuthorizationId: number) => {
  const { dispatch } = useApplicationInfoContext();
  const { exactAdministraties, loading: exactLoading, error: exactError } = useExactAdministraties(
    systemAuthorizationId
  );
  const { easyflexAdministraties, loading: easyflexADLoading, error: easyFlexADError } = useEasyflexAdministraties();
  const { easyflexJournals, loading: easyflexJournalsLoading, error: easyflexJournalsError } = useEasyflexJournals();
  const { easyflexVATs, loading: easyflexVATsLoading, error: easyflexVATsError } = useEasyflexVATCodes();

  const loading =
    exactLoading ||
    easyflexADLoading ||
    easyflexJournalsLoading ||
    easyflexVATsLoading;

  const error =
    exactError ||
    easyFlexADError ||
    easyflexJournalsError ||
    easyflexVATsError;

  useEffect(() => {
    if (!loading && !error) {
      dispatch({ type: "UPDATE_DESTINATIONS", destinations: exactAdministraties });
      dispatch({ type: "UPDATE_EASYFLEX_ADMINS", admins: easyflexAdministraties });
      dispatch({ type: "UPDATE_JOURNALS", journals: easyflexJournals });
      dispatch({ type: "UPDATE_EASYFLEX_VAT_CODES", codes: easyflexVATs });
    }
  }, [loading, error, exactAdministraties, easyflexAdministraties, easyflexJournals, easyflexVATs, dispatch]);

  return { loading, error };
};

// Get ISO week number for a date
export const getISOWeek = (date: Date): number => {
  const d = new Date(date.getTime());
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 4 - (d.getDay() || 7));
  const yearStart = new Date(d.getFullYear(), 0, 1);
  return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
};

// Get previous week's year and week number (ISO 8601)
export const getPreviousWeek = (): { year: number; week: number } => {
  const now = new Date();
  const prevWeekDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  const week = getISOWeek(prevWeekDate);
  // ISO week year: if week is >= 52 and month is January, it belongs to previous year
  let year = prevWeekDate.getFullYear();
  if (week >= 52 && prevWeekDate.getMonth() === 0) {
    year--;
  } else if (week === 1 && prevWeekDate.getMonth() === 11) {
    year++;
  }
  return { year, week };
};

// Get current week's year and week number (ISO 8601)
export const getCurrentWeek = (): { year: number; week: number } => {
  const now = new Date();
  const week = getISOWeek(now);
  let year = now.getFullYear();
  if (week >= 52 && now.getMonth() === 0) {
    year--;
  } else if (week === 1 && now.getMonth() === 11) {
    year++;
  }
  return { year, week };
};

// Get the number of ISO weeks in a given year (52 or 53)
export const getWeeksInYear = (year: number): number => {
  // A year has 53 weeks if Jan 1 is Thursday, or Dec 31 is Thursday
  const jan1 = new Date(year, 0, 1);
  const dec31 = new Date(year, 11, 31);
  return jan1.getDay() === 4 || dec31.getDay() === 4 ? 53 : 52;
};
